import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Radio,
  FormControl,
  Chip,
  Alert,
  Container,
  Stack,
} from '@mui/material';
import {
  CreditCard,
  Add,
  Delete,
  Edit,
  AccountBalance,
  LocalAtm,
  Security,
} from '@mui/icons-material';
import { useSnackbar } from '../contexts/SnackbarContext';
import { userAPI } from '../services/api';

interface PaymentMethod {
  id: string;
  type: 'card' | 'cash';
  isDefault: boolean;
  cardInfo?: {
    last4: string;
    brand: string;
    expiryMonth: number;
    expiryYear: number;
  };
}

const PaymentPage: React.FC = () => {
  const { showSnackbar } = useSnackbar();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [cardForm, setCardForm] = useState({
    cardNumber: '',
    holderName: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    isDefault: false,
  });
  const [editingCard, setEditingCard] = useState<PaymentMethod | null>(null);

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    try {
      setIsLoading(true);
      const response = await userAPI.getPaymentMethods();

      if (response && response.success && response.data) {
        setPaymentMethods(response.data);
      } else {
        setPaymentMethods([]);
      }
    } catch (error: any) {
      console.error('Failed to fetch payment methods:', error);
      if (error?.response?.status !== 404) {
        showSnackbar('Ödəniş üsulları yüklənə bilmədi', 'error');
      }
      setPaymentMethods([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddPaymentMethod = () => {
    setIsAddModalOpen(true);
    setEditingCard(null);
    setCardForm({
      cardNumber: '',
      holderName: '',
      expiryMonth: '',
      expiryYear: '',
      cvv: '',
      isDefault: false,
    });
  };

  const handleEditPaymentMethod = (method: PaymentMethod) => {
    setEditingCard(method);
    if (method.type === 'card' && method.cardInfo) {
      setCardForm({
        cardNumber: `****-****-****-${method.cardInfo.last4}`,
        holderName: '',
        expiryMonth: method.cardInfo.expiryMonth.toString().padStart(2, '0'),
        expiryYear: method.cardInfo.expiryYear.toString(),
        cvv: '',
        isDefault: method.isDefault,
      });
      setIsAddModalOpen(true);
    }
  };

  const handleDeletePaymentMethod = async (methodId: string) => {
    if (window.confirm('Bu ödəniş üsulunu silmək istədiyinizə əminsiniz?')) {
      try {
        const response = await userAPI.deletePaymentMethod(methodId);

        if (response.success) {
          showSnackbar('Ödəniş üsulu silindi', 'success');
          fetchPaymentMethods();
        } else {
          showSnackbar(response.message || 'Ödəniş üsulu silinə bilmədi', 'error');
        }
      } catch (error) {
        console.error('Failed to delete payment method:', error);
        showSnackbar('Ödəniş üsulu silinə bilmədi', 'error');
      }
    }
  };

  const handleSavePaymentMethod = async () => {
    const cardNumberDigits = cardForm.cardNumber.replace(/\s/g, '');
    if (cardNumberDigits.length < 16) {
      showSnackbar('Düzgün kart nömrəsi daxil edin (16 rəqəm)', 'error');
      return;
    }

    if (!cardForm.holderName.trim()) {
      showSnackbar('Kart sahibinin adını daxil edin', 'error');
      return;
    }

    if (!cardForm.expiryMonth || !cardForm.expiryYear) {
      showSnackbar('Kartın son istifadə tarixini daxil edin', 'error');
      return;
    }

    if (cardForm.cvv.length < 3) {
      showSnackbar('CVV kodunu daxil edin', 'error');
      return;
    }

    try {
      const methodData = {
        type: 'card',
        isDefault: cardForm.isDefault,
        cardInfo: {
          cardNumber: cardForm.cardNumber.replace(/\s/g, ''),
          holderName: cardForm.holderName,
          expiryMonth: parseInt(cardForm.expiryMonth),
          expiryYear: parseInt(cardForm.expiryYear),
          cvv: cardForm.cvv,
        },
      };

      const response = await userAPI.addPaymentMethod(methodData);

      if (response.success) {
        showSnackbar(editingCard ? 'Ödəniş üsulu yeniləndi' : 'Ödəniş üsulu əlavə edildi', 'success');
        setIsAddModalOpen(false);
        fetchPaymentMethods();
      } else {
        showSnackbar(response.message || 'Ödəniş üsulu əlavə edilə bilmədi', 'error');
      }
    } catch (error) {
      console.error('Failed to add payment method:', error);
      showSnackbar('Ödəniş üsulu əlavə edilə bilmədi', 'error');
    }
  };

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 16) {
      value = value.slice(0, 16);
    }
    const formatted = value.replace(/(\d{4})(?=\d)/g, '$1 ');
    setCardForm(prev => ({ ...prev, cardNumber: formatted }));
  };

  const getCardIcon = (brand: string) => {
    switch (brand.toLowerCase()) {
      case 'visa':
        return '💳';
      case 'mastercard':
        return '💳';
      default:
        return '💳';
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3 }, px: { xs: 2, sm: 3 } }}>
      {}
      <Box sx={{ mb: { xs: 3, sm: 4 } }}>
        <Typography 
          variant="h4" 
          component="h1" 
          gutterBottom
          sx={{ 
            fontWeight: 700,
            background: 'linear-gradient(135deg, #FF006E 0%, #2196F3 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontSize: { xs: '1.75rem', sm: '2rem' },
          }}
        >
          Ödəniş Üsulları
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
          Sifarişləriniz üçün istifadə edəcəyiniz ödəniş üsullarını idarə edin
        </Typography>
      </Box>

      {}
      <Card 
        sx={{ 
          mb: { xs: 3, sm: 4 },
          borderRadius: { xs: 3, sm: 4 },
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.05)',
          bgcolor: 'primary.main',
          color: 'white',
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
          <Box display="flex" alignItems="center" mb={1}>
            <Security sx={{ mr: 1.5, fontSize: { xs: 24, sm: 28 } }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Təhlükəsiz ödəniş
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ opacity: 0.9, fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
            Ödəniş məlumatlarınız təhlükəsiz şəkildə saxlanılır və üçüncü tərəflərlə paylaşılmır
          </Typography>
        </CardContent>
      </Card>

      {}
      <Box mb={3}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddPaymentMethod}
          size="large"
          sx={{ 
            borderRadius: 2,
            px: { xs: 3, sm: 4 },
            py: { xs: 1.75, sm: 2 },
            minHeight: { xs: 48, sm: 52 },
            fontSize: { xs: '0.95rem', sm: '1rem' },
            fontWeight: 600,
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
            '&:active': {
              transform: 'scale(0.98)',
              transition: 'transform 0.1s ease-out',
            },
          }}
        >
          Yeni Ödəniş Üsulu Əlavə Et
        </Button>
      </Box>

      {}
      {isLoading ? (
        <Stack spacing={2}>
          {Array.from({ length: 3 }).map((_, index) => (
            <Card key={index} sx={{ borderRadius: { xs: 3, sm: 4 } }}>
              <CardContent sx={{ p: 3 }}>
                <Box height={80} />
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : paymentMethods.length > 0 ? (
        <Stack spacing={2}>
          {paymentMethods.map((method) => (
            <Card 
              key={method.id}
              sx={{
                borderRadius: { xs: 3, sm: 4 },
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(0, 0, 0, 0.05)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                },
              }}
            >
              <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item>
                    {method.type === 'card' && method.cardInfo ? (
                      <Box
                        sx={{
                          width: { xs: 56, sm: 64 },
                          height: { xs: 40, sm: 48 },
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          fontSize: { xs: 28, sm: 32 },
                          bgcolor: 'primary.main',
                          color: 'white',
                          borderRadius: 2,
                          boxShadow: '0 4px 12px rgba(255, 0, 110, 0.3)',
                        }}
                      >
                        {getCardIcon(method.cardInfo.brand)}
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          width: { xs: 56, sm: 64 },
                          height: { xs: 40, sm: 48 },
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          bgcolor: 'grey.200',
                          borderRadius: 2,
                        }}
                      >
                        <LocalAtm sx={{ fontSize: { xs: 28, sm: 32 }, color: 'text.secondary' }} />
                      </Box>
                    )}
                  </Grid>

                  <Grid item xs>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {method.type === 'card' && method.cardInfo
                        ? `**** **** **** ${method.cardInfo.last4}`
                        : 'Nağd pul'}
                    </Typography>
                    {method.type === 'card' && method.cardInfo && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        {method.cardInfo.brand} • Son istifadə: {method.cardInfo.expiryMonth.toString().padStart(2, '0')}/
                        {method.cardInfo.expiryYear.toString().slice(-2)}
                      </Typography>
                    )}
                    {method.isDefault && (
                      <Chip 
                        label="Standart" 
                        size="small" 
                        color="primary" 
                        sx={{ 
                          height: 24,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}
                      />
                    )}
                  </Grid>

                  <Grid item>
                    <Stack direction="row" spacing={1}>
                      <IconButton
                        color="primary"
                        onClick={() => handleEditPaymentMethod(method)}
                        disabled={method.type === 'cash'}
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          '&:hover': { bgcolor: 'primary.dark' },
                          '&.Mui-disabled': {
                            bgcolor: 'grey.300',
                            color: 'grey.500',
                          },
                        }}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDeletePaymentMethod(method.id)}
                        disabled={method.type === 'cash'}
                        sx={{
                          bgcolor: 'error.main',
                          color: 'white',
                          '&:hover': { bgcolor: 'error.dark' },
                          '&.Mui-disabled': {
                            bgcolor: 'grey.300',
                            color: 'grey.500',
                          },
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <Card
          sx={{
            borderRadius: { xs: 3, sm: 4 },
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(0, 0, 0, 0.05)',
          }}
        >
          <CardContent sx={{ textAlign: 'center', py: { xs: 5, sm: 6 } }}>
            <Box
              sx={{
                width: { xs: 80, sm: 100 },
                height: { xs: 80, sm: 100 },
                borderRadius: '50%',
                bgcolor: 'grey.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <AccountBalance sx={{ fontSize: { xs: 40, sm: 50 }, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
              Qeyd edilmiş ödəniş üsulu yoxdur
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Sifarişləri daha asanlaşdırmaq üçün ödəniş üsulu əlavə edin
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddPaymentMethod}
              size="large"
              sx={{ 
                borderRadius: 2,
                px: 4,
                py: 1.5,
                fontSize: { xs: '0.95rem', sm: '1rem' },
                fontWeight: 600,
              }}
            >
              Ödəniş Üsulu Əlavə Et
            </Button>
          </CardContent>
        </Card>
      )}

      {}
      <Dialog
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 3, sm: 4 },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
          {editingCard ? 'Ödəniş Üsulunu Redaktə Et' : 'Yeni Ödəniş Üsulu Əlavə Et'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Kart Nömrəsi"
              value={cardForm.cardNumber}
              onChange={handleCardNumberChange}
              inputProps={{ 
                maxLength: 19,
                inputMode: 'numeric',
                pattern: '[0-9 ]*',
              }}
              placeholder="4242 4242 4242 4242"
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
              InputProps={{
                startAdornment: (
                  <CreditCard sx={{ mr: 1, color: 'text.secondary' }} />
                ),
              }}
            />

            <TextField
              fullWidth
              label="Kart Sahibinin Adı"
              value={cardForm.holderName}
              onChange={(e) => setCardForm(prev => ({ ...prev, holderName: e.target.value }))}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Ay"
                  value={cardForm.expiryMonth}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 12)) {
                      setCardForm(prev => ({ ...prev, expiryMonth: value.slice(0, 2) }));
                    }
                  }}
                  inputProps={{ 
                    maxLength: 2,
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  placeholder="12"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="İl"
                  value={cardForm.expiryYear}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setCardForm(prev => ({ ...prev, expiryYear: value.slice(0, 4) }));
                  }}
                  inputProps={{ 
                    maxLength: 4,
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  placeholder="2025"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="CVV"
              value={cardForm.cvv}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                setCardForm(prev => ({ ...prev, cvv: value.slice(0, 3) }));
              }}
              inputProps={{ 
                maxLength: 3,
                inputMode: 'numeric',
                pattern: '[0-9]*',
              }}
              placeholder="123"
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />

            <FormControl>
              <FormControlLabel
                control={
                  <Radio
                    checked={cardForm.isDefault}
                    onChange={(e) => setCardForm(prev => ({ ...prev, isDefault: e.target.checked }))}
                  />
                }
                label="Standart ödəniş üsulu kimi təyin et"
              />
            </FormControl>
          </Stack>

          <Alert 
            severity="info" 
            sx={{ 
              mt: 3,
              borderRadius: 2,
            }}
            icon={<Security />}
          >
            Ödəniş məlumatlarınız təhlükəsiz şəkildə saxlanılır və üçüncü tərəflərlə paylaşılmır.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 }, pt: 1 }}>
          <Button 
            onClick={() => setIsAddModalOpen(false)}
            sx={{ 
              borderRadius: 2,
              minHeight: { xs: 56, sm: 48 },
              py: { xs: 2, sm: 1.5 },
              px: { xs: 3, sm: 2 },
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'transparent',
              '&:active': {
                transform: 'scale(0.98)',
                transition: 'transform 0.1s ease-out',
              },
            }}
          >
            Ləğv et
          </Button>
          <Button 
            onClick={handleSavePaymentMethod} 
            variant="contained"
            sx={{ 
              borderRadius: 2,
              px: { xs: 3, sm: 2 },
              py: { xs: 2, sm: 1.5 },
              minHeight: { xs: 56, sm: 48 },
              fontWeight: 600,
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'transparent',
              '&:active': {
                transform: 'scale(0.98)',
                transition: 'transform 0.1s ease-out',
              },
            }}
          >
            {editingCard ? 'Yadda saxla' : 'Əlavə et'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PaymentPage;
